/**
 * Notification Service
 * Handles notification fetching, WebSocket connection, and polling
 */

import apiClient from './apiConfig';
import { toast } from 'react-toastify';

const API_BASE = import.meta.env.VITE_BACKEND_URL || '';

class NotificationService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.pollInterval = null;
    this.pollFrequency = 300000; // 5 minutes (300 seconds)
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000; // 3 seconds
    this.listeners = new Map();
  }

  /**
   * Initialize WebSocket connection (optional, requires socket.io on backend)
   */
  async initWebSocket(token) {
    try {
      // Check if socket.io is available
      if (window.io) {
        this.socket = window.io(`${API_BASE}`, {
          auth: { token },
          reconnection: true,
          reconnectionDelay: this.reconnectDelay,
          reconnectionDelayMax: 10000,
          reconnectionAttempts: this.maxReconnectAttempts
        });

        this.socket.on('connect', () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.emit('connected');
        });

        this.socket.on('new-notification', (notification) => {
          this.emit('notification', notification);

          // Show toast for high priority notifications
          if (notification.priority === 'high') {
            toast.info(`${notification.title}: ${notification.message}`, {
              position: 'bottom-right',
              autoClose: 5000
            });
          }
        });

        this.socket.on('disconnect', () => {
          this.isConnected = false;
          this.emit('disconnected');
          // Fallback to polling
          this.startPolling(token);
        });

        this.socket.on('error', (error) => {
          this.emit('error', error);
        });
      } else {
        // socket.io not available, use polling
        this.startPolling(token);
      }
    } catch (error) {
      // Fallback to polling
      this.startPolling(token);
    }
  }

  /**
   * Start polling for notifications
   */
  startPolling(token) {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }

    // Poll function
    const poll = async () => {
      try {
        const response = await apiClient.get('/api/notifications', {
          params: { limit: 10, status: 'unread' }
        });

        const { unreadCount } = response.data;

        // Only emit unread count update (not individual notifications to avoid duplicates)
        // Individual notifications are emitted via WebSocket for real-time updates
        this.emit('unreadCount', unreadCount);
      } catch (error) {
        // Silently fail - polling will retry on next interval
        console.error('Polling error:', error);
      }
    };

    // Execute immediately on start
    poll();

    // Then continue polling at regular intervals
    this.pollInterval = setInterval(poll, this.pollFrequency);
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
    this.stopPolling();
  }

  /**
   * Subscribe to notification events
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Emit event to listeners
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          // Silently fail - callback error shouldn't affect other listeners
        }
      });
    }
  }

  /**
   * Fetch notifications
   */
  async fetchNotifications(token, limit = 20, offset = 0, status = null) {
    try {
      const params = { limit, offset };
      if (status) params.status = status;

      const response = await apiClient.get('/api/notifications', { params });
      return response.data;
    } catch (error) {
      console.error('Fetch notifications error:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(token, notificationId) {
    try {
      const response = await apiClient.post(`/api/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Mark as read error:', error);
      throw error;
    }
  }

  /**
   * Archive notification
   */
  async archiveNotification(token, notificationId) {
    try {
      const response = await apiClient.post(`/api/notifications/${notificationId}/archive`);
      return response.data;
    } catch (error) {
      console.error('Archive notification error:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(token, notificationId) {
    try {
      const response = await apiClient.delete(`/api/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  }

  /**
   * Track notification click
   */
  async trackClick(token, notificationId, actionUrl) {
    try {
      await apiClient.post(`/api/notifications/${notificationId}/track-click`, { actionUrl });
    } catch (error) {
      // Don't throw, this is not critical
      console.error('Track click error:', error);
    }
  }

  /**
   * Get notification settings
   */
  async getSettings(token) {
    try {
      const response = await apiClient.get('/api/notifications/settings');
      return response.data;
    } catch (error) {
      console.error('Get settings error:', error);
      throw error;
    }
  }

  /**
   * Update notification settings
   */
  async updateSettings(token, settings) {
    try {
      const response = await apiClient.post('/api/notifications/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Update settings error:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new NotificationService();
